import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { DeliveryShift } from "app/types/subscriptionsManager";
import type { DeliveryAreaList } from "app/types";
import {
  DeliveryShiftRestrictionRow,
  normalizePutRestrictionsDistricts,
  PutDeliveryShiftRestrictionsPayload,
} from "app/types/deliveryShiftConfiguration";
import { getDeliveryShiftsAPI } from "app/services/subscriptions";
import {
  getDeliveryAreaAPI,
  getDeliveryShiftRestrictionsAPI,
  putDeliveryShiftRestrictionsAPI,
} from "app/services/configuration";
import { errorHandler } from "app/shared/Service/errorHandler";
import { alertMessage } from "../actions/common";
import { AppDispatch } from "../store";
import type { RootState } from "../reducers/hooks";
import i18n from "app/config/i18n";
import { getActiveConfig } from "app/utils/constants/common";
import {
  flattenCountryDistrictsPayload,
  mapCountryDistrictsToDeliveryAreaList,
} from "app/types/countryDistrictsApi";

interface ConfigurationSliceState {
  deliveryShifts: DeliveryShift[] | null;
  deliveryShiftRestrictions: DeliveryShiftRestrictionRow[] | null;
  deliveryDistricts: DeliveryAreaList[] | null;
  cachedDistrictsCountryId: number | null;
  cachedShiftsStoreId: number | null;
  loading: boolean;
  saving: boolean;
}

const initialState: ConfigurationSliceState = {
  deliveryShifts: null,
  deliveryShiftRestrictions: null,
  deliveryDistricts: null,
  cachedDistrictsCountryId: null,
  cachedShiftsStoreId: null,
  loading: false,
  saving: false,
};

async function fetchDistrictsListForCountry(
  countryId: number,
): Promise<DeliveryAreaList[]> {
  const { data: body } = await getDeliveryAreaAPI(countryId);
  const flat = flattenCountryDistrictsPayload(body.data);
  return mapCountryDistrictsToDeliveryAreaList(flat);
}

const configurationSlice = createSlice({
  name: "configurationSlice",
  initialState,
  reducers: {
    setDeliveryShiftsLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setDeliveryShiftsSaving: (state, action: PayloadAction<boolean>) => {
      state.saving = action.payload;
    },
    setDeliveryShifts: (state, action: PayloadAction<DeliveryShift[]>) => {
      state.deliveryShifts = action.payload;
    },
    setDeliveryShiftRestrictions: (
      state,
      action: PayloadAction<DeliveryShiftRestrictionRow[]>,
    ) => {
      state.deliveryShiftRestrictions = action.payload;
    },
    setDeliveryDistrictsForShifts: (
      state,
      action: PayloadAction<{ list: DeliveryAreaList[]; countryId: number }>,
    ) => {
      state.deliveryDistricts = action.payload.list;
      state.cachedDistrictsCountryId = action.payload.countryId;
    },
    setCachedShiftsStoreId: (state, action: PayloadAction<number | null>) => {
      state.cachedShiftsStoreId = action.payload;
    },
    mergeDeliveryShiftRestriction: (
      state,
      action: PayloadAction<DeliveryShiftRestrictionRow>,
    ) => {
      if (!state.deliveryShiftRestrictions) {
        state.deliveryShiftRestrictions = [action.payload];
        return;
      }
      const idx = state.deliveryShiftRestrictions.findIndex(
        (r) => r.deliveryShiftId === action.payload.deliveryShiftId,
      );
      if (idx === -1) {
        state.deliveryShiftRestrictions.push(action.payload);
      } else {
        state.deliveryShiftRestrictions[idx] = action.payload;
      }
    },
    resetDeliveryShiftConfiguration: () => initialState,
  },
});

export const {
  setDeliveryShiftsLoading,
  setDeliveryShiftsSaving,
  setDeliveryShifts,
  setDeliveryShiftRestrictions,
  setDeliveryDistrictsForShifts,
  setCachedShiftsStoreId,
  mergeDeliveryShiftRestriction,
  resetDeliveryShiftConfiguration,
} = configurationSlice.actions;

export const loadDeliveryShiftsTabData =
  (options?: { force?: boolean }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const force = options?.force ?? false;
    const { storeId, activeLocale } = getActiveConfig();
    const countryId = activeLocale?.CountryId;

    if (!storeId) {
      dispatch(setDeliveryShifts([]));
      dispatch(setDeliveryShiftRestrictions([]));
      dispatch(
        setDeliveryDistrictsForShifts({
          list: [],
          countryId: countryId ?? 0,
        }),
      );
      dispatch(setCachedShiftsStoreId(null));
      return;
    }

    const s = getState().configurationSlice;

    const needDistricts =
      Boolean(countryId) &&
      (force ||
        !s.deliveryDistricts ||
        s.cachedDistrictsCountryId !== countryId);

    const needShiftsBundle =
      force ||
      s.cachedShiftsStoreId !== storeId ||
      s.deliveryShifts === null ||
      s.deliveryShiftRestrictions === null;

    if (!needDistricts && !needShiftsBundle) {
      return;
    }

    try {
      dispatch(setDeliveryShiftsLoading(true));

      const runDistricts = async () => {
        if (!needDistricts || !countryId) {
          return;
        }
        try {
          const list = await fetchDistrictsListForCountry(countryId);
          dispatch(setDeliveryDistrictsForShifts({ list, countryId }));
        } catch (error) {
          const message = errorHandler(error);
          dispatch(alertMessage(message, "error"));
          dispatch(setDeliveryDistrictsForShifts({ list: [], countryId }));
        }
      };

      const runShifts = async () => {
        if (!needShiftsBundle) {
          return;
        }
        try {
          const [shiftsRes, restrictionsRes] = await Promise.all([
            getDeliveryShiftsAPI(storeId),
            getDeliveryShiftRestrictionsAPI(storeId),
          ]);
          dispatch(setDeliveryShifts(shiftsRes.data.data ?? []));
          dispatch(
            setDeliveryShiftRestrictions(restrictionsRes.data.data ?? []),
          );
          dispatch(setCachedShiftsStoreId(storeId));
        } catch (error) {
          const message = errorHandler(error);
          dispatch(alertMessage(message, "error"));
          dispatch(setDeliveryShifts([]));
          dispatch(setDeliveryShiftRestrictions([]));
        }
      };

      await Promise.all([runDistricts(), runShifts()]);
    } finally {
      dispatch(setDeliveryShiftsLoading(false));
    }
  };

export const updateDeliveryShiftRestrictions =
  ({
    deliveryShiftId,
    payload,
  }: {
    deliveryShiftId: number;
    payload: PutDeliveryShiftRestrictionsPayload;
  }) =>
  async (dispatch: AppDispatch): Promise<boolean> => {
    const { storeId } = getActiveConfig();
    if (!storeId) {
      return false;
    }
    try {
      dispatch(setDeliveryShiftsSaving(true));
      const { data } = await putDeliveryShiftRestrictionsAPI(
        storeId,
        deliveryShiftId,
        payload,
      );
      const districts = normalizePutRestrictionsDistricts(data.data);
      dispatch(
        mergeDeliveryShiftRestriction({
          deliveryShiftId: data.data.deliveryShiftId,
          restrictedDistricts: districts,
        }),
      );
      dispatch(
        alertMessage(
          i18n.t("common.deliveryShiftRestrictionsUpdated"),
          "success",
        ),
      );
      return true;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
      return false;
    } finally {
      dispatch(setDeliveryShiftsSaving(false));
    }
  };

export default configurationSlice.reducer;
